/**
 * Centralised page-dimension configuration for the DOCX editor.
 *
 * Every pixel value that describes page geometry lives here so that
 * both TypeScript layout logic and CSS (via custom properties) share
 * a single source of truth.  Swap presets to support different paper
 * sizes without touching any other file.
 */

// ── Types ──────────────────────────────────────────────────────────

export interface PageMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface PageConfig {
  /** Human-readable label for the page size. */
  label?: string;
  /** Total page width in CSS px (e.g. 816 for 8.5 in at 96 dpi). */
  pageWidth: number;
  /** Total page height in CSS px (e.g. 1056 for 11 in at 96 dpi). */
  pageHeight: number;
  /** Page margins in CSS px. */
  margins: PageMargins;
  /** Visual gap between consecutive page backgrounds in CSS px. */
  pageGap: number;
}

// ── Derived helpers ────────────────────────────────────────────────

export function contentWidth(c: PageConfig): number {
  return c.pageWidth - c.margins.left - c.margins.right;
}

export function contentHeight(c: PageConfig): number {
  return c.pageHeight - c.margins.top - c.margins.bottom;
}

/**
 * Height of the spacer decoration inserted between pages.
 * bottom-margin + gap + top-margin of the next page.
 */
export function spacerHeight(c: PageConfig): number {
  return c.margins.bottom + c.pageGap + c.margins.top;
}

/**
 * Total pixel height occupied by `n` pages including inter-page gaps.
 * This is the outer height of the page-backgrounds area.
 */
export function totalPagesHeight(c: PageConfig, pageCount: number): number {
  return pageCount * c.pageHeight + Math.max(0, pageCount - 1) * c.pageGap;
}

/**
 * The min-height that the inner content div needs so that, together
 * with the content-area padding (= margins), the wrapper exactly
 * matches the page-backgrounds area.
 *
 *   padding-top + innerMinHeight + padding-bottom = totalPagesHeight
 */
export function contentAreaInnerMinHeight(c: PageConfig, pageCount: number): number {
  return totalPagesHeight(c, pageCount) - c.margins.top - c.margins.bottom;
}

// ── Presets ─────────────────────────────────────────────────────────

/** US Letter — 8.5 × 11 in at 96 dpi, 1 in margins on every side. */
export const US_LETTER: PageConfig = {
  label: "Letter",
  pageWidth: 816,
  pageHeight: 1056,
  margins: { top: 96, right: 96, bottom: 96, left: 96 },
  pageGap: 8,
};

/** ISO A4 — 210 × 297 mm at 96 dpi, 1 in margins. */
export const A4: PageConfig = {
  label: "A4",
  pageWidth: 794,
  pageHeight: 1123,
  margins: { top: 96, right: 96, bottom: 96, left: 96 },
  pageGap: 8,
};

/** US Legal — 8.5 × 14 in at 96 dpi, 1 in margins. */
export const US_LEGAL: PageConfig = {
  label: "Legal",
  pageWidth: 816,
  pageHeight: 1344,
  margins: { top: 96, right: 96, bottom: 96, left: 96 },
  pageGap: 8,
};

/** US Letter with narrow margins (0.5 in). */
export const US_LETTER_NARROW: PageConfig = {
  label: "Letter (narrow)",
  pageWidth: 816,
  pageHeight: 1056,
  margins: { top: 48, right: 48, bottom: 48, left: 48 },
  pageGap: 8,
};

/** US Letter with wide margins (1.25 in left/right). */
export const US_LETTER_WIDE: PageConfig = {
  label: "Letter (wide)",
  pageWidth: 816,
  pageHeight: 1056,
  margins: { top: 96, right: 120, bottom: 96, left: 120 },
  pageGap: 8,
};

/** All available page presets for the page setup UI. */
export const PAGE_PRESETS: PageConfig[] = [
  US_LETTER,
  US_LETTER_NARROW,
  US_LETTER_WIDE,
  A4,
  US_LEGAL,
];

// ── CSS custom-property bridge ─────────────────────────────────────

/**
 * Write all page-dimension tokens as CSS custom properties on `el`
 * so that `docx-editor.css` can reference them via `var(--page-*)`.
 */
export function applyPageCSSVars(el: HTMLElement, c: PageConfig): void {
  const s = el.style;
  s.setProperty("--page-width", `${c.pageWidth}px`);
  s.setProperty("--page-height", `${c.pageHeight}px`);
  s.setProperty("--page-margin-top", `${c.margins.top}px`);
  s.setProperty("--page-margin-right", `${c.margins.right}px`);
  s.setProperty("--page-margin-bottom", `${c.margins.bottom}px`);
  s.setProperty("--page-margin-left", `${c.margins.left}px`);
  s.setProperty("--page-content-width", `${contentWidth(c)}px`);
  s.setProperty("--page-content-height", `${contentHeight(c)}px`);
  s.setProperty("--page-gap", `${c.pageGap}px`);
  s.setProperty("--page-spacer-height", `${spacerHeight(c)}px`);
}
